import { useState, useEffect, useMemo } from "react";
import { UserCog, Users, TrendingUp, AlertTriangle, Target } from "lucide-react";
import { VentureCard, VentureCardHeader, VentureCardTitle, VentureCardContent } from "@/components/ui/venture-card";
import { BadgeVenture } from "@/components/ui/badge-venture";
import { supabase } from "@/integrations/supabase/client";

interface CloserRecord {
  id: string;
  name: string;
  email: string;
  weekly_target: number;
  performance_score: number | null;
  status: string;
}

interface AssignmentRecord {
  id: string;
  closer_id: string;
  client_id: string;
  weekly_target: number;
  status: string;
  closers: CloserRecord | null;
  clients: { company_name: string } | null;
}

interface CloserPerformance {
  closerId: string;
  closerName: string;
  closerEmail: string;
  weeklyTarget: number;
  performanceScore: number | null;
  assignedClients: string[];
}

export function CloserAssignmentsTab() {
  const [performances, setPerformances] = useState<CloserPerformance[]>([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    async function fetchAssignments() {
      setLoading(true);
      try {
        const { data, error } = await supabase
          .from('closer_assignments')
          .select('*, closers(*), clients(company_name)')
          .eq('status', 'active');

        if (error) {
          console.error('Error fetching closer assignments:', error);
          setPerformances([]);
          return;
        }

        // Group assignments by closer
        const closerMap = new Map<string, CloserPerformance>();
        (data as AssignmentRecord[] || []).forEach((assignment) => {
          const closer = assignment.closers;
          if (!closer) return;

          if (!closerMap.has(closer.id)) {
            closerMap.set(closer.id, {
              closerId: closer.id,
              closerName: closer.name,
              closerEmail: closer.email,
              weeklyTarget: closer.weekly_target,
              performanceScore: closer.performance_score,
              assignedClients: [],
            });
          }
          const clientName = assignment.clients?.company_name;
          if (clientName) {
            closerMap.get(closer.id)!.assignedClients.push(clientName);
          }
        });

        setPerformances(Array.from(closerMap.values()));
      } catch (err) {
        console.error('Error fetching closer assignments:', err);
        setPerformances([]);
      } finally {
        setLoading(false);
      }
    }

    fetchAssignments();
  }, []);

  if (loading) {
    return (
      <div className="space-y-6">
        <VentureCard>
          <VentureCardContent>
            <div className="text-center py-12">
              <div className="animate-pulse space-y-4">
                <div className="h-4 bg-muted rounded w-1/3 mx-auto" />
                <div className="h-4 bg-muted rounded w-1/2 mx-auto" />
              </div>
            </div>
          </VentureCardContent>
        </VentureCard>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Closer Assignments Table */}
      <VentureCard>
        <VentureCardHeader>
          <VentureCardTitle className="flex items-center gap-2">
            <Users className="w-5 h-5 text-secondary" />
            Closer Assignments & Performance
          </VentureCardTitle>
        </VentureCardHeader>
        <VentureCardContent>
          {performances.length === 0 ? (
            <div className="text-center py-12">
              <Users className="w-12 h-12 mx-auto text-muted-foreground mb-4" />
              <p className="text-muted-foreground">No active closer assignments</p>
              <p className="text-sm text-muted-foreground mt-1">
                Assignments will appear here when closers are assigned to clients.
              </p>
            </div>
          ) : (
            <div className="overflow-x-auto">
              <table className="w-full min-w-[600px]">
                <thead>
                  <tr className="border-b border-border">
                    <th className="text-left py-3 px-4 text-sm font-medium text-muted-foreground">Closer</th>
                    <th className="text-left py-3 px-4 text-sm font-medium text-muted-foreground">Assigned Clients</th>
                    <th className="text-center py-3 px-4 text-sm font-medium text-muted-foreground">Weekly Target</th>
                    <th className="text-center py-3 px-4 text-sm font-medium text-muted-foreground">Performance Score</th>
                  </tr>
                </thead>
                <tbody>
                  {performances.map((perf) => (
                    <tr key={perf.closerId} className="border-b border-border hover:bg-surface transition-colors">
                      <td className="py-3 px-4">
                        <div className="flex items-center gap-3">
                          <div className="w-10 h-10 rounded-full bg-primary/20 flex items-center justify-center">
                            <UserCog className="w-5 h-5 text-primary" />
                          </div>
                          <div>
                            <p className="font-medium">{perf.closerName}</p>
                            <p className="text-xs text-muted-foreground">{perf.closerEmail}</p>
                          </div>
                        </div>
                      </td>
                      <td className="py-3 px-4">
                        <div className="flex flex-wrap gap-1">
                          {perf.assignedClients.map((name, i) => (
                            <BadgeVenture key={i} variant="default">{name}</BadgeVenture>
                          ))}
                        </div>
                      </td>
                      <td className="py-3 px-4 text-center font-medium">{perf.weeklyTarget}</td>
                      <td className="py-3 px-4 text-center">
                        {perf.performanceScore != null ? (
                          <span className={
                            perf.performanceScore >= 80 ? 'text-success font-bold' :
                            perf.performanceScore >= 60 ? 'text-warning font-medium' :
                            'text-destructive'
                          }>
                            {perf.performanceScore}
                          </span>
                        ) : (
                          <span className="text-muted-foreground">N/A</span>
                        )}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </VentureCardContent>
      </VentureCard>

      {/* Weekly Targets */}
      <VentureCard>
        <VentureCardHeader>
          <VentureCardTitle className="flex items-center gap-2">
            <Target className="w-5 h-5 text-secondary" />
            Weekly Close Targets
          </VentureCardTitle>
        </VentureCardHeader>
        <VentureCardContent>
          {performances.length === 0 ? (
            <div className="text-center py-8">
              <p className="text-muted-foreground">No closers assigned yet.</p>
            </div>
          ) : (
            <>
              <p className="text-sm text-muted-foreground mb-6">Weekly targets per closer</p>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                {performances.map((perf) => (
                  <div key={perf.closerId} className="p-4 rounded-lg border border-border">
                    <div className="flex items-center justify-between mb-3">
                      <div className="flex items-center gap-3">
                        <div className="w-10 h-10 rounded-full bg-primary/20 flex items-center justify-center">
                          <UserCog className="w-5 h-5 text-primary" />
                        </div>
                        <span className="font-medium">{perf.closerName}</span>
                      </div>
                      <BadgeVenture variant="info">
                        Target: {perf.weeklyTarget}
                      </BadgeVenture>
                    </div>
                    <div className="flex flex-wrap gap-1 mt-2">
                      {perf.assignedClients.map((name, i) => (
                        <BadgeVenture key={i} variant="default">{name}</BadgeVenture>
                      ))}
                    </div>
                  </div>
                ))}
              </div>
            </>
          )}
        </VentureCardContent>
      </VentureCard>
    </div>
  );
}
